import React, { useEffect, useState } from "react";
import { Form, Divider, Typography, Radio, Card } from "antd";

import classes from "../AddBanner.module.scss";
import MediaUploadCard from "../components/MediaUploadCard";
import { useHistory } from "react-router-dom";

import { useBannerActions } from "../hooks/useBannerActions";
import { buildBannerPayload } from "../utils/buildBannerPayload";
import { uploadConfig } from "../constants/uploadConfig";

import Title from "antd/lib/typography/Title";
import ButtonActions from "../../../../../Components/Buttons/ButtonActions/ButtonActions";
import Header from "../../../../../Components/Header/Header";

const AddBannerPage2 = ({
  handleCancel,
  uploads,
  setUploads,
  bannerData,
  backPage,
}) => {
  // controla a tela selecionada (panel ou activity_screen)
  const [selected, setSelected] = useState("panel");

  // mensagem de erro da validação
  const [errorMessage, setErrorMessage] = useState("");

  // loading do botão finalizar
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);

  // verifica se está em modo edição
  const isEditingBanner = !!bannerData?.banner_id;

  const history = useHistory();
  // controla os passos da tela
  const [step, setStep] = useState(1);
  const { createBanner, updateBanner } = useBannerActions();

  // quando for edição, mantém a tela já cadastrada
  useEffect(() => {
  if (bannerData?.displayed_screen) {
    setSelected(bannerData.displayed_screen);
  }
}, []);

  // atualiza o upload quando o usuário envia uma nova imagem
  const handleUploadChange = (id, file) => {
    if (file) {
      const previewUrl = URL.createObjectURL(file);

      //Atualiza o estado de uploads
      setUploads((prev) =>
        prev.map(
          (item) =>
            item.id === id
              ? { ...item, file, preview: previewUrl } // atualiza o arquivo do item certo
              : item, // mantém os outros iguais
        ),
      );
    }
  };

  // valida se todos os uploads obrigatórios foram preenchidos
  const validateUploads = () => {
    // pega apenas uploads da tela selecionada
    const selectedUploads = uploads.filter(
      (item) => item.displayed_screen === selected,
    );

    // verifica se existe arquivo novo OU imagem já salva
    const hasError = selectedUploads.some(
      (item) => !item.file && !bannerData?.urls?.[item.id],
    );

    if (hasError) {
      setErrorMessage(
        "Verifique se todos os formatos de banner foram adicionados.",
      );

      setTimeout(() => {
        setErrorMessage("");
      }, 3000);

      return false;
    }
    setErrorMessage("");
    return true;
  };

  // cria ou edita o banner
  const handleSubmit = async () => {
    const isValid = validateUploads();
    if (!isValid) return;

    try {
      setIsLoadingSubmit(true);
      // monta o payload para enviar para API
      const payload = buildBannerPayload({
        bannerData,
        uploads,
        selected,
      });

      // se tiver banner_id → edição
      // se não → cadastro novo
      if (isEditingBanner) {
        await updateBanner(payload);
      } else {
        await createBanner(payload);
      }

      history.push("/admin/banners");
    } catch (error) {
      console.log("Erro ao adicionar banner: ", error);
      setIsLoadingSubmit(false);
    }
  };

  // monta o preview da imagem no card de upload
  const getFile = (id) => {
    // procura o upload pelo id
    const item = uploads.find((item) => item.id === id);

    // se o usuário já enviou uma nova imagem, mostra essa imagem
    if (item?.file) {
      return {
        uid: id,
        name: item.file.name,
        url: item.preview,
        originFileObj: item.file,
      };
    }

    // se estiver editando, pega a imagem que já veio salva
    const existingImage = bannerData?.urls?.[id];

    // se existir imagem cadastrada, mostra ela no card
    if (existingImage) {
      return {
        uid: id,
        name: id,
        url: existingImage,
      };
    }
    return undefined;
  };

  // renderiza os cards de upload de acordo com a tela selecionada
  const renderUploadCards = () => (
    <div className={classes.bannerMediaOptions}>
      {uploadConfig[selected].map((item) => (
        <MediaUploadCard
          key={item.id}
          name={item.id}
          value={getFile(item.id)}
          text={item.text}
          icon={item.icon}
          onChange={(file) => handleUploadChange(item.id, file)}
        />
      ))}
    </div>
  );

  // primeira etapa: escolher onde o banner será exibido
  const renderStepOne = () => {
    return (
      <>
        <Title level={4}>Selecione em qual tela o banner será exibido</Title>
        <div className={classes.optionSelector}>
          <Card
            className={`${classes.optionCard} ${
              selected === "panel" ? classes.active : ""
            }`}
            onClick={!isEditingBanner ? () => setSelected("panel") : undefined}
          >
            <div>
              <Radio
                checked={selected === "panel"}
                disabled={isEditingBanner}
              />
              <Typography.Text className={classes.RadioTitle}>
                PAINEL
              </Typography.Text>
            </div>

            <Typography.Text className={classes.RadioText}>
              Exibir Banner no Painel Inicial do usuário
            </Typography.Text>
          </Card>

          <Card
            className={`${classes.optionCard} ${
              selected === "activity_screen" ? classes.active : ""
            }`}
            onClick={
              !isEditingBanner
                ? () => setSelected("activity_screen")
                : undefined
            }
          >
            <div>
              <Radio
                checked={selected === "activity_screen"}
                disabled={isEditingBanner}
              />
              <Typography.Text className={classes.RadioTitle}>
                TELA DE ATIVIDADE
              </Typography.Text>
            </div>

            <Typography.Text className={classes.RadioText}>
              Exibir Banner na tela de atividades
            </Typography.Text>
          </Card>
        </div>
        {isEditingBanner && (
          <Typography.Text
            type="secondary"
            style={{
              display: "block",
              marginTop: "10px",
            }}
          >
            *A tela de exibição não pode ser alterada após o cadastro.
          </Typography.Text>
        )}
        <Divider style={{ borderWidth: "2px", marginBottom: "50px" }} />
      </>
    );
  };

  // segunda etapa: upload das imagens
  const renderStepTwo = () => {
    return (
      <>
        <Title level={4}>
          Envie versões do banner para os tamanhos de tela abaixo:
        </Title>
        {renderUploadCards()}
      </>
    );
  };

  const handleBack = () => {
    // se estiver no passo 2, volta para o passo 1
    if (step === 2) {
      setStep(1);
      return;
    }
    // se estiver no passo 1, volta para página anterior
    backPage();
  };

  return (
    <div>
      <Header
        text={isEditingBanner ? "Editar Banner" : "Cadastrar Banner"}
      />

      {step === 1 && renderStepOne()}
      {step === 2 && renderStepTwo()}

      {/* mensagem de erro */}
      <div style={{ minHeight: "24px", marginTop: "8px" }}>
        {errorMessage && (
          <Typography.Text type="danger">{errorMessage}</Typography.Text>
        )}
      </div>

      <Form.Item wrapperCol={{ offset: 4, span: 16 }}>
        <div className={classes.buttonContainer}>
          <ButtonActions variant="outline" onClick={handleBack} text="Voltar" />

          {step === 1 && (
            <ButtonActions
              variant="primary"
              onClick={() => setStep(2)}
              text="Continuar"
            />
          )}

          {/* botão finalizar */}
          {step === 2 && (
            <ButtonActions
              variant="primary"
              onClick={handleSubmit}
              disabled={isLoadingSubmit}
              loading={isLoadingSubmit}
              text={isEditingBanner ? " Finalizar Edição" : "Finalizar Cadastro"}
            />
          )}
        </div>
      </Form.Item>
    </div>
  );
};

export default AddBannerPage2;
